import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';

function LevelMeter({
  value,
  min,
  max,
  size = 140,
  optimalMin,
  optimalMax,
  segments = 10,
  label,
  color,
}) {
  const normalizedValue = Math.min(Math.max((value - min) / (max - min), 0), 1);
  const activeSegments = Math.round(normalizedValue * segments);

  const height = size;
  const width = Math.max(20, size * 0.5);
  const segmentHeight = Math.max(2, (height - 30) / segments);
  const segmentGap = Math.max(1, size * 0.02);

  // Scale factor for fonts (based on default size of 140)
  const scale = size / 140;

  // Get color for each segment based on its position, or use custom color
  const getSegmentColor = (index, isActive) => {
    if (!isActive) return '#1f2937';

    // If custom color is provided, use it for all active segments
    if (color) return color;

    const segmentValue = min + ((index + 1) / segments) * (max - min);

    if (segmentValue < optimalMin) {
      // Below optimal - gradient from yellow to green
      return '#eab308';
    } else if (segmentValue > optimalMax) {
      // Above optimal - gradient from green to red
      const overRatio = (segmentValue - optimalMax) / (max - optimalMax);
      if (overRatio > 0.5) return '#ef4444';
      return '#f97316';
    }
    // In optimal range
    return '#22c55e';
  };

  // SVG dimensions - meter bar is centered, scale markers positioned to the side
  const svgWidth = width + 60;
  const meterX = (svgWidth - width) / 2; // Center the meter bar horizontally

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: size,
      height: height + 20,
    }}>
      <svg width={svgWidth} height={height + 20} viewBox={`0 0 ${svgWidth} ${height + 20}`}>
        <defs>
          <filter id="levelGlow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          <linearGradient id="segmentShine" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.2)" />
            <stop offset="50%" stopColor="rgba(255,255,255,0.4)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0.1)" />
          </linearGradient>
        </defs>

        {/* Container background - centered */}
        <rect
          x={meterX}
          y={10}
          width={width}
          height={height - 20}
          rx={8}
          fill="#111827"
          stroke="#374151"
          strokeWidth={2}
        />

        {/* Optimal range indicator on the side */}
        {optimalMin !== undefined && optimalMax !== undefined && (
          <rect
            x={meterX - 8}
            y={10 + (height - 20) * (1 - (optimalMax - min) / (max - min))}
            width={4}
            height={(height - 20) * ((optimalMax - optimalMin) / (max - min))}
            fill="#22c55e"
            opacity={0.6}
            rx={2}
          />
        )}

        {/* Segments - centered */}
        {Array.from({ length: segments }).map((_, index) => {
          const isActive = index < activeSegments;
          const segmentY = 15 + (segments - 1 - index) * segmentHeight;

          return (
            <g key={index}>
              <rect
                x={meterX + 5}
                y={segmentY}
                width={Math.max(1, width - 10)}
                height={Math.max(1, segmentHeight - segmentGap)}
                rx={Math.min(4, Math.max(1, width - 10) / 2)}
                fill={getSegmentColor(index, isActive)}
                opacity={isActive ? 1 : 0.3}
                filter={isActive ? 'url(#levelGlow)' : undefined}
              />
              {isActive && (
                <rect
                  x={meterX + 5}
                  y={segmentY}
                  width={Math.max(1, width - 10)}
                  height={Math.max(1, segmentHeight - segmentGap)}
                  rx={Math.min(4, Math.max(1, width - 10) / 2)}
                  fill="url(#segmentShine)"
                  opacity={0.5}
                />
              )}
            </g>
          );
        })}

        {/* Scale markers - positioned to the right of meter */}
        {[0, 0.5, 1].map((tick, i) => (
          <g key={i}>
            <text
              x={meterX + width + 8}
              y={15 + (height - 30) * (1 - tick) + 4}
              fill="#9ca3af"
              fontSize={Math.max(7, 9 * scale)}
              textAnchor="start"
            >
              {Math.round(min + (max - min) * tick)}
            </text>
          </g>
        ))}

        {/* Label */}
        {label && (
          <text
            x={svgWidth / 2}
            y={height + 8}
            fill="#9ca3af"
            fontSize={Math.max(8, 10 * scale)}
            textAnchor="middle"
          >
            {label}
          </text>
        )}
      </svg>
    </Box>
  );
}

LevelMeter.propTypes = {
  value: PropTypes.number.isRequired,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  size: PropTypes.number,
  optimalMin: PropTypes.number,
  optimalMax: PropTypes.number,
  segments: PropTypes.number,
  label: PropTypes.string,
  color: PropTypes.string,
};

export default LevelMeter;
